import EnumService from "../../../jwt/enum.service"

/*
 * Regole UI per gli esiti delle queue incarico.
 *
 * Flusso allineato al dominio incarico:
 * - Incarico completato e selezionabile solo se esiste almeno un task
 *   pagamento spesa associato all'incarico.
 * - Nella queue "INCARICO NOMINATO", da APERTO restano selezionabili:
 *   - Perizia negativa
 *   - Incarico confermato
 *   - In agenda
 * - Perizia negativa non è selezionabile se l'incarico è già confermato.
 * - Incarico annullato e Incarico rifiutato sono consentiti
 *   solo se non esistono pagamenti spesa collegati all'incarico.
 * - Sugli incarichi conferiti tramite portale fiduciari (portalEnabled = true)
 *   Incarico confermato, Perizia negativa e Incarico rifiutato non sono mai
 *   selezionabili: conferma e perizia negativa arrivano con la restituzione della
 *   perizia gestita dal job check-restituzione-incarico, il rifiuto lo esprime il
 *   fiduciario sul portale.
 * - Chiusura incarico è consentita solo quando la situazione economica
 *   è già chiusa:
 *   - totale pagamenti spesa = riserva spesa incarico
 *   - oppure riserva e pagamenti entrambi a zero
 *
 * La funzione restituisce true se l'esito deve essere disabilitato.
 */

/**
 * gestioneEsitiIncaricoNominatoHook
 * 
 * @description
 * La funzione gestisce gli esiti possibili per le queue incarico; il flag
 * distingue le regole aggiuntive proprie dell'incarico nominato.
 * I parametri che vengono passati sono:
 * - esito: l'oggetto esito contenente le informazioni dell'esito da verificare
 * - listaPagamentiSpesaPerIncarico: array contenente i task pagamento relativi all'incarico.
 *   La presenza di un task indica che il pagamento ha gia raggiunto il flusso
 *   "Invio ordine di pagamento" o "Pagamento effettuato"; il FE non deve
 *   verificarne ulteriormente l'esito.
 * - importoTotalePagamentiSpesaPerIncarico: somma dell'importo dei pagamenti relativi all'incarico
 * - rispervaSpesaIncarico: riserva spesa assegnata all'incarico
 * - criterioIncaricoNominato: booleano che indica se applicare anche le regole post-nomina
 * - statoIncarico: stato corrente dell'incarico
 * - portalEnabled: true se l'incarico è stato conferito tramite portale fiduciari
 * 
 * La funzione restituisce true se l'esito passato deve essere disabilitato, false altrimenti.
 * 
 * @param {Object} esito
 * @param {Array} listaPagamentiSpesaPerIncarico
 * @param {Number} importoTotalePagamentiSpesaPerIncarico
 * @param {Number} rispervaSpesaIncarico
 * @param {Boolean} criterioIncaricoNominato
 * @param {String} statoIncarico
 * @param {Boolean} portalEnabled
 * @returns {Boolean}
 */
const gestioneEsitiIncaricoNominatoHook = (esito, listaPagamentiSpesaPerIncarico, importoTotalePagamentiSpesaPerIncarico, rispervaSpesaIncarico, criterioIncaricoNominato, statoIncarico, portalEnabled = false) => {
  const normalizedEsitoDescription = esito.description.toLowerCase();
  const normalizedStatoIncarico = statoIncarico?.toUpperCase();
  const isEsitoCompletato = EnumService.ESITI.INCARICO_COMPLETATO?.includes(normalizedEsitoDescription);
  const isEsitoConfermato = EnumService.ESITI.INCARICO_CONFERMATO?.includes(normalizedEsitoDescription);
  const isEsitoRifiutato = EnumService.ESITI.INCARICO_RIFIUTATO?.includes(normalizedEsitoDescription);
  const isEsitoAnnullato = EnumService.ESITI.INCARICO_ANNULLATO?.includes(normalizedEsitoDescription);
  const isEsitoPeriziaNegativa = EnumService.ESITI.PERIZIA_NEGATIVA?.includes(normalizedEsitoDescription);
  const isEsitoChiusura = EnumService.ESITI.CHIUSURA_INCARICO?.includes(normalizedEsitoDescription);
  const isEsitoAgenda = EnumService.ESITI.IN_AGENDA?.includes(normalizedEsitoDescription);
  const hasPagamentiCollegati = (listaPagamentiSpesaPerIncarico?.length ?? 0) > 0;
  const isSituazioneEconomicaChiusa = (
    ((listaPagamentiSpesaPerIncarico?.length ?? 0) > 0 && Number(importoTotalePagamentiSpesaPerIncarico) === Number(rispervaSpesaIncarico)) ||
    (Number(importoTotalePagamentiSpesaPerIncarico) === 0 && Number(rispervaSpesaIncarico) === 0)
  );

  // Portal assignments: confirmation and negative perizia are set by the
  // check-restituzione-incarico job when the perizia comes back, refusal is
  // expressed by the fiduciario on the portal.
  if (portalEnabled === true && (isEsitoConfermato || isEsitoPeriziaNegativa || isEsitoRifiutato)) {
    return true;
  }

  // Completamento, rifiuto e annullamento dipendono dalla presenza del pagamento
  // per tutte le queue incarico, nominate e non nominate.
  if (isEsitoCompletato) {
    return !hasPagamentiCollegati;
  }

  if (isEsitoRifiutato || isEsitoAnnullato) {
    return hasPagamentiCollegati;
  }

  if(criterioIncaricoNominato){
    // La chiusura finale è disponibile solo quando la parte economica è già coerente.
    if (isEsitoChiusura) {
      return !isSituazioneEconomicaChiusa;
    }

    if (isEsitoPeriziaNegativa && ['EVASO', 'CONFERMATO'].includes(normalizedStatoIncarico)) {
      return true;
    }

    // Gli altri esiti operativi del post-nomina restano sempre selezionabili.
    if (
      isEsitoConfermato ||
      isEsitoPeriziaNegativa ||
      isEsitoAgenda
    ) {
      return false;
    }

    // Fuori dal set consentito, in incarico nominato lasciamo attiva solo la validazione legacy.
    return !EnumService.ESITI.IN_VALIDAZIONE?.includes(normalizedEsitoDescription);
  }

  // Sulle queue non "incarico nominato" preserviamo il comportamento storico.
  return EnumService.ESITI.IN_VALIDAZIONE?.includes(normalizedEsitoDescription);
}

export default gestioneEsitiIncaricoNominatoHook
